<script setup>
/* eslint-disable vue/no-unused-properties, vue/no-unused-emit-declarations */
// audioSilenceThresholdMs prop/emit reserved for future audio processing feature
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Slider from 'dashboard/components-next/slider/Slider.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  responseDelayMs: {
    type: [Number, String],
    default: 0,
  },
  audioSilenceThresholdMs: {
    type: [Number, String],
    default: 2000,
  },
  aggregateMessages: {
    type: Boolean,
    default: false,
  },
  aggregationWindowMs: {
    type: [Number, String],
    default: 3000,
  },
  responseMode: {
    type: Object,
    default: () => ({
      text_to: 'text',
      audio_to: 'text',
      image_to: 'text',
      video_to: 'text',
      file_to: 'text',
    }),
  },
});

const emit = defineEmits([
  'update:responseDelayMs',
  'update:audioSilenceThresholdMs',
  'update:aggregateMessages',
  'update:aggregationWindowMs',
  'update:responseMode',
]);

const { t } = useI18n();

// Helper to convert string to number
const toNumber = val => (typeof val === 'string' ? parseInt(val, 10) : val);

// Computed values with two-way binding
const responseDelayValue = computed({
  get: () => toNumber(props.responseDelayMs),
  set: val => emit('update:responseDelayMs', toNumber(val)),
});

const aggregateValue = computed({
  get: () => props.aggregateMessages,
  set: val => emit('update:aggregateMessages', val),
});

const aggregationWindowValue = computed({
  get: () => toNumber(props.aggregationWindowMs),
  set: val => emit('update:aggregationWindowMs', toNumber(val)),
});

// Response mode helpers
const responseModeValue = computed(() => props.responseMode || {});

const updateResponseMode = (inputType, value) => {
  const newMode = { ...responseModeValue.value };
  newMode[`${inputType}_to`] = value;
  emit('update:responseMode', newMode);
};

const getResponseMode = inputType => {
  return responseModeValue.value[`${inputType}_to`] || 'text';
};

// Input types configuration
const inputTypes = computed(() => [
  {
    key: 'text',
    icon: 'i-lucide-type',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.INPUT_TYPES.TEXT'),
  },
  {
    key: 'audio',
    icon: 'i-lucide-mic',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.INPUT_TYPES.AUDIO'),
  },
  {
    key: 'image',
    icon: 'i-lucide-image',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.INPUT_TYPES.IMAGE'),
  },
  {
    key: 'video',
    icon: 'i-lucide-video',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.INPUT_TYPES.VIDEO'),
  },
  {
    key: 'file',
    icon: 'i-lucide-file',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.INPUT_TYPES.FILE'),
  },
]);

const responseOptions = computed(() => [
  {
    value: 'text',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.OUTPUT_TYPES.TEXT'),
    icon: 'i-lucide-type',
  },
  {
    value: 'audio',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.OUTPUT_TYPES.AUDIO'),
    icon: 'i-lucide-volume-2',
  },
  {
    value: 'both',
    label: t('AI_AGENTS.RESPONSE_SETTINGS.OUTPUT_TYPES.BOTH'),
    icon: 'i-lucide-layers',
  },
]);

// Format milliseconds to seconds for display
const formatMs = ms => {
  const seconds = ms / 1000;
  const count = seconds === 1 ? 1 : parseFloat(seconds.toFixed(1));
  return seconds === 1
    ? t('AI_AGENTS.RESPONSE_SETTINGS.TIME.SECOND', { count })
    : t('AI_AGENTS.RESPONSE_SETTINGS.TIME.SECONDS', { count });
};
</script>

<template>
  <div class="space-y-6">
    <!-- Timing Settings Section -->
    <div class="space-y-5">
      <div class="flex items-center gap-2 mb-4">
        <span class="i-lucide-timer size-5 text-n-brand" />
        <h4 class="text-base font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.RESPONSE_SETTINGS.TIMING_TITLE') }}
        </h4>
      </div>

      <!-- Response Delay -->
      <div class="space-y-3 pb-5 border-b border-n-weak">
        <div class="flex items-center justify-between">
          <div>
            <label class="text-sm font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.RESPONSE_SETTINGS.RESPONSE_DELAY') }}
            </label>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ t('AI_AGENTS.RESPONSE_SETTINGS.RESPONSE_DELAY_DESC') }}
            </p>
          </div>
          <span
            class="text-sm font-bold font-mono text-n-brand bg-n-brand/10 px-2 py-1 rounded min-w-[5rem] text-center"
          >
            {{ formatMs(responseDelayValue) }}
          </span>
        </div>
        <Slider
          v-model="responseDelayValue"
          :min="0"
          :max="10000"
          :step="500"
        />
        <div class="flex justify-between text-xs font-medium text-n-slate-10">
          <span>{{ t('AI_AGENTS.RESPONSE_SETTINGS.IMMEDIATE') }}</span>
          <span>{{ t('AI_AGENTS.RESPONSE_SETTINGS.TEN_SECONDS') }}</span>
        </div>
      </div>

      <!-- Aggregate Messages -->
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <label class="text-sm font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.RESPONSE_SETTINGS.AGGREGATE_MESSAGES') }}
            </label>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{ t('AI_AGENTS.RESPONSE_SETTINGS.AGGREGATE_MESSAGES_DESC') }}
            </p>
          </div>
          <Switch v-model="aggregateValue" />
        </div>

        <!-- Aggregation Window (only if aggregate is enabled) -->
        <div v-if="aggregateValue" class="pl-4 border-l-2 border-n-brand/30">
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div>
                <label class="text-sm font-medium text-n-slate-11">
                  {{ t('AI_AGENTS.RESPONSE_SETTINGS.AGGREGATION_WINDOW') }}
                </label>
              </div>
              <span
                class="text-sm font-bold font-mono text-n-slate-12 bg-n-alpha-2 px-2 py-1 rounded"
              >
                {{ formatMs(aggregationWindowValue) }}
              </span>
            </div>
            <Slider
              v-model="aggregationWindowValue"
              :min="1000"
              :max="15000"
              :step="1000"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Response Mode Section -->
    <div class="space-y-4 pt-2">
      <div class="flex items-center gap-2 mb-4">
        <span class="i-lucide-repeat size-5 text-n-brand" />
        <h4 class="text-base font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.RESPONSE_SETTINGS.MODE_TITLE') }}
        </h4>
      </div>
      <p class="text-xs text-n-slate-11 -mt-2 mb-4">
        {{ t('AI_AGENTS.RESPONSE_SETTINGS.MODE_DESC') }}
      </p>

      <!-- Response Mode Grid -->
      <div class="space-y-3">
        <div
          v-for="inputType in inputTypes"
          :key="inputType.key"
          class="p-3 bg-n-alpha-1 rounded-lg space-y-2"
        >
          <!-- Input Type Label -->
          <div class="flex items-center gap-2">
            <span :class="inputType.icon" class="size-4 text-n-slate-11" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ inputType.label }}
            </span>
          </div>

          <!-- Response Options: grupo segmentado que preenche a largura -->
          <div class="flex gap-1.5">
            <button
              v-for="option in responseOptions"
              :key="option.value"
              type="button"
              class="flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium transition-all"
              :class="
                getResponseMode(inputType.key) === option.value
                  ? 'bg-n-brand text-white shadow-sm'
                  : 'bg-n-solid-2 text-n-slate-11 border border-n-weak hover:border-n-brand/50 hover:text-n-brand'
              "
              @click="updateResponseMode(inputType.key, option.value)"
            >
              <span :class="option.icon" class="size-3.5" />
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Info message -->
    <p
      class="text-xs text-n-slate-11 bg-n-alpha-2 rounded-lg p-3 border-l-4 border-n-brand"
    >
      {{ t('AI_AGENTS.RESPONSE_SETTINGS.MODE_INFO') }}
    </p>
  </div>
</template>
